import React, { useState, useEffect, useRef } from "react";
import { useDeploymentViewModel } from "../ViewModel/DeploymentViewModel";

interface DeploymentStatusTrackerProps {
  deploymentId: string;
  isOpen: boolean;
  onClose: () => void;
  isNewDeployment?: boolean;
}

interface StatusLog {
  status: "info" | "success" | "error" | "warning";
  message: string;
  timestamp: string;
}

interface WebSocketMessage {
  deployment_id: string;
  status: string;
  message: string;
  timestamp: string;
}

const DeploymentStatusTracker: React.FC<DeploymentStatusTrackerProps> = ({
  deploymentId,
  isOpen,
  onClose,
  isNewDeployment = false,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [logs, setLogs] = useState<StatusLog[]>([]);
  const socketRef = useRef<WebSocket | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [latestStatus, setLatestStatus] = useState<"info" | "success" | "error" | "warning">("info");
  const [showNotificationSlider, setShowNotificationSlider] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [deploymentProgress, setDeploymentProgress] = useState(0);

  const { getDeploymentLogs } = useDeploymentViewModel();

  // Calculate progress based on logs
  useEffect(() => {
    if (logs.length > 0) {
      const successLogs = logs.filter(log => log.status === 'success').length;
      const totalLogs = logs.length;
      const progress = Math.min((successLogs / totalLogs) * 100, 100);
      setDeploymentProgress(progress);
    }
  }, [logs]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Play notification sound
  const playNotificationSound = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const showSliderNotification = (message: string) => {
    setNotificationMessage(message);
    setShowNotificationSlider(true);
    
    try {
      playNotificationSound();
    } catch (error) {
      console.log("Could not play notification sound:", error);
    }
    
    setTimeout(() => {
      setShowNotificationSlider(false);
    }, 5000);
  };

  const handleDeploymentCompletion = () => {
    showSliderNotification("Deployment Completed Successfully! 🎉");
    setDeploymentProgress(100);

    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const addStatusLog = (status: Omit<StatusLog, "timestamp">) => {
    const timestamp = new Date().toISOString();
    setLogs((prevLogs) => [...prevLogs, { ...status, timestamp }]);
    setLatestStatus(status.status);
  };

  const connectToDeployment = () => {
    if (!deploymentId) return;

    if (socketRef.current) {
      socketRef.current.close();
    }

    setLogs([
      {
        status: "info",
        message: `🚀 Initializing deployment ${deploymentId}...`,
        timestamp: new Date().toISOString(),
      },
    ]);

    const wsUrl = `wss://${process.env.REACT_APP_WS_HOST}/ws/deployments/${deploymentId}`;
    console.log("WS URL ", wsUrl);

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      setIsConnected(true);
      addStatusLog({
        status: "success",
        message: `✓ Connected to deployment stream`,
      });
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;
        console.log("DATA", data);
        
        if (data.deployment_id && data.status && data.message && data.timestamp) {
          const statusType = data.status === "completed" ? "success" : 
                            data.status === "failed" ? "error" : 
                            data.status === "running" ? "info" : "info";

          addStatusLog({
            status: statusType,
            message: data.message,
          });

          if (data.message === "Deployment Completed") {
            handleDeploymentCompletion();
          }
        } else {
          const status = data as StatusLog;
          if (["info", "success", "error", "warning"].includes(status.status)) {
            addStatusLog(status);
            
            if (status.message === "Deployment Completed") {
              handleDeploymentCompletion();
            }
          } else {
            console.warn("Unknown status type:", status);
          }
        }
      } catch (error) {
        console.error("Invalid WebSocket message format:", error);
        addStatusLog({
          status: "error",
          message: "Received invalid message format",
        });
      }
    };

    socket.onclose = (event) => {
      setIsConnected(false);
      
      const statusMessage = event.code === 1008 ? 
        "❌ Authentication failed: Invalid token" : 
        "⚠ Disconnected from deployment";
      
      addStatusLog({
        status: event.code === 1008 ? "error" : "warning",
        message: statusMessage,
      });
      
      socketRef.current = null;
    };

    socket.onerror = () => {
      setIsConnected(false);
      addStatusLog({
        status: "error",
        message: "❌ WebSocket connection error",
      });
    };
  };

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await getDeploymentLogs(deploymentId);
        if (response && Array.isArray(response)) {
          const formattedLogs = response.map((log: string) => ({
            status: "info" as const,
            message: log,
            timestamp: new Date().toISOString(),
          }));
          setLogs(formattedLogs);
          if (formattedLogs.length > 0) {
            setLatestStatus("info");
          }
        } else {
          setLogs([
            {
              status: "warning" as const,
              message: "No logs available for this deployment",
              timestamp: new Date().toISOString(),
            },
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch deployment logs:", error);
        setLogs([
          {
            status: "error" as const,
            message: "Failed to fetch deployment logs",
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    };

    if (deploymentId && isOpen) {
      if (isNewDeployment) {
        connectToDeployment();
      } else {
        fetchLogs();
      }
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [deploymentId, isOpen, isNewDeployment, getDeploymentLogs]);

  if (!isOpen) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return "✓";
      case "error":
        return "✗";
      case "warning":
        return "⚠";
      default:
        return "→";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "text-green-400";
      case "error":
        return "text-red-400";
      case "warning":
        return "text-yellow-400";
      default:
        return "text-blue-400";
    }
  };

  const isCloseDisabled = isNewDeployment && isConnected;

  return (
    <>
      {/* Notification Slider */}
      {showNotificationSlider && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl max-w-sm">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{notificationMessage}</p>
              </div>
              <button
                onClick={() => setShowNotificationSlider(false)}
                className="flex-shrink-0 ml-2 text-white hover:text-gray-200"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Tracker - Inline */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-lg flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className="text-white font-semibold text-sm">
              Deployment Logs
            </span>
            {isNewDeployment && (
              <span className={`text-xs px-2 py-1 rounded ${
                isConnected ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
              }`}>
                {isConnected ? 'LIVE' : 'OFFLINE'}
              </span>
            )}
          </div>
          
          <button
            onClick={onClose}
            disabled={isCloseDisabled}
            className={`text-xl font-bold ${
              isCloseDisabled 
                ? 'text-gray-600 cursor-not-allowed' 
                : 'text-gray-400 hover:text-white'
            }`}
            title={isCloseDisabled ? "Cannot close while monitoring live deployment" : "Close"}
          >
            ×
          </button>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-700 h-1">
          <div 
            className="h-1 bg-green-500 transition-all duration-300"
            style={{ width: `${deploymentProgress}%` }}
          ></div>
        </div>

        {/* Logs Container */}
        <div
          ref={logContainerRef}
          className="flex-1 p-4 overflow-y-auto bg-gray-900 font-mono text-sm"
          style={{ scrollBehavior: 'smooth', maxHeight: '500px' }}
        >
          {logs.map((log, index) => (
            <div key={index} className="mb-2 flex items-start space-x-2 hover:bg-gray-800 px-2 py-1 rounded">
              <span className={`${getStatusColor(log.status)} font-bold flex-shrink-0`}>
                {getStatusIcon(log.status)}
              </span>
              <span className="text-gray-300 flex-1 break-words">{log.message}</span>
              {log.timestamp && (
                <span className="text-gray-500 text-xs flex-shrink-0">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
              )}
            </div>
          ))}
          
          {isConnected && isNewDeployment && (
            <div className="flex items-center mt-4">
              <div className="h-3 w-1 bg-green-400 animate-pulse mr-2"></div>
              <span className="text-green-400 text-xs">Listening for updates...</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default DeploymentStatusTracker;